import React from 'react';
import get from 'lodash/get';
import omit from 'lodash/omit';
import includes from 'lodash/includes';
import {FormattedMessage} from 'react-intl';
import {byStringField, formatDate, states} from 'utils/DBUtils';
import {enUSmessage} from 'i18n/en-US.js';

export const getSearchableUserResources = configs => Object.keys(configs).map(route => {
  const {filterResource} = configs[route];
  return filterResource;
});


// not a Redux selector, just a helper for when we have the user profile itself.
export const getGroupName = user => get(user, 'userProfile.group.name', '');

export const dateTimeFormatString = 'MMM D, YYYY HH:mm:ss A';

export const formatDateTime = ({date: {year, month, day}, time: {hour, minute, second}}) => {
  const date = new Date(year, month - 1, day, hour, minute, second);
  return formatDate(date, dateTimeFormatString);
};

const queryIsEmpty = query => !(query && Object.keys(query).some(field => query[field]));

const queryOperators = {
  email: 'Like',
  uid: 'Equal',
  username: 'Like',
  firstName: 'Like',
  lastName: 'Like',
  commentId: 'Equal',
  adminsOnly: 'Equal',
  siteId: 'Equal'
};

export const BEAKER_USER_ROLE_ID = 1;

export const userIsAdmin = (user) => user.roleId === BEAKER_USER_ROLE_ID;

export const buildQueryFilters = query => {
  const queryFilters = queryIsEmpty(query) ? [] : Object.keys(query).reduce((filters, field) => {
    const value = query[field];

    if (value) {
      const filter = {};

      if (field === 'adminsOnly' && value === true) {
        // query User resource for admin status specifically
        filter.field = 'roles';
        filter.operator = queryOperators[field];
        filter.value = BEAKER_USER_ROLE_ID;
      }
      else if (Array.isArray(value) && value.length > 0) {
        // query SurveyUserAnswer resource
        filter.field = field === 'state' ? field : `questionAnswers.${field}`;
        filter.operator = 'In';
        filter.value = value.join(',');
      }
      else {
        // query User resource
        filter.field = field === 'uid' ? 'id' : field;
        filter.operator = queryOperators[field];
        filter.value = value;
      }

      filters.push(filter);
    }

    return filters;
  }, []);

  return queryFilters;
};

export const buildHubFilters = query => buildQueryFilters(omit(query, ['selectAll', 'deselectAll']));

// showing the country name for now. this may change, if El Dailybreak gets its own Site resource.
export const countriesBySiteId = {
  'd13a8764-6797-498b-8c8c-5c1808787bd9': 'US',
  'edd2a819-03c8-43a5-9bb0-380aa2c97144': 'UK'
};

export const SectionedColumns = {
  basicInfo: [
    'Select',
    'Photo',
    'UserId',
    'FullName',
    'Email',
    'AgeGroup',
    'Gender',
    'JoinDate'
  ],
  demographics: [
    'HouseholdIncome',
    'EmploymentStatus',
    'MaritalStatus',
    'HomeType',
    'HouseholdMembers',
    'State'
  ],
  socialPlatforms: [
    'Facebook',
    'Instagram',
    'Pinterest',
    'Snapchat',
    'Twitter'
  ],
  recentActivities: [
    'TravelDomesticLeisure',
    'TravelDomesticWork',
    'TravelInternationalLeisure',
    'TravelInternationalWork',
    'HaveFinancialAdvisor',
    'HavePersonalTrainer',
    'MemberHealthClub',
    'MemberBookOrWineClub',
    'MemberGameNight',
    'MemberFantasySports',
    'EducationProfessionalDevelopment',
    'EducationPersonalDevelopment',
    'PurchasedLifeInsurance',
    'PurchasedFinancialProduct'
  ],
  healthAttitude: [
    'NeverExercise',
    'RarelyExercise',
    'AverageExercise',
    'FrequentlyExercise'
  ]
};

const SectionedFilters = {
  basicInfo: [
    'GENDER',
    'MARITAL_STATUS',
    'AGE_RANGE'
  ],
  demographics: [
    'EMPLOYMENT_STATUS',
    'HOUSEHOLD_INCOME_RANGE',
    'LIVING_SITUATION',
    'HOUSEHOLD_SIZE'
  ],
  socialPlatforms: [
    'SOCIAL_PLATFORM_USAGE'
  ],
  recentActivities: [
    'RECENT_ACTIVITIES'
  ],
  healthAttitude: [
    'HEALTH_ATTITUDE'
  ]
};

const sectionFieldsFactory = fields => sectionName => {
  const section = SectionedFilters[sectionName];
  return fields.filter(field => includes(section, field.question));
};

export const generateHubFormConfig = hubSurvey => {
  if (!hubSurvey) {
    return {
      sections: []
    };
  }

  // survey questions and answers to filter on
  const hubFilters = hubSurvey.surveyQuestions.map(question => {
    const answers = question.surveyAnswers
      .filter(answer => answer.answer !== 'DOES_NOT_APPLY')
      .map(answer => ({
        answer: answer.answer,
        key: answer.id,
        value: String(answer.id),
        text: <FormattedMessage id={`humanology.survey.answer.${answer.answer}`} />
      }));

    return {
      question: question.question,
      key: question.id,
      type: 'checkbox',
      name: String(question.id),
      props: {
        label: <p><FormattedMessage id={`humanology.survey.question.${question.question}`} /></p>,
        className: 'hub-filter',
        colCount: 1,
        colWidth: 16,
        options: answers
      }
    };
  });

  // allow filtering by US state
  const usStates = {
    type: 'checkbox',
    name: 'state',
    props: {
      label: <p><FormattedMessage id='humanology.survey.filter.state' /></p>,
      className: 'hub-filter',
      options: states
        .map(({abbrev}) => ({
          key: abbrev,
          value: abbrev,
          text: abbrev
        }))
        .sort(byStringField('value'))
    }
  };

  const sectionFields = sectionFieldsFactory(hubFilters);

  return {
    sections: [
      {
        props: {
          header: 'Basic Info'
        },
        fields: sectionFields('basicInfo').concat(usStates)
      },
      {
        props: {
          header: 'Demographics'
        },
        fields: sectionFields('demographics')
      },
      {
        props: {
          header: 'Social Platform Usage'
        },
        fields: sectionFields('socialPlatforms')
      },
      {
        props: {
          header: 'Recent Activities'
        },
        fields: sectionFields('recentActivities')
      },
      {
        props: {
          header: 'Health/Fitness'
        },
        fields: sectionFields('healthAttitude')

      }
    ]
  };
};

export const findQuestion = (surveyQuestions, questionKey) => surveyQuestions.find(q => q.question === questionKey);

export const findAnswers = (userAnswers, questionId) => {
  const match = Object.keys(userAnswers).find(k => Number(k) === questionId);
  return userAnswers[match];
};

export const findAnswerById = (questionAnswers, answerId) => questionAnswers.find(a => a.id === answerId);

export const findAnswerByKey = (questionAnswers, answerKey) => questionAnswers.find(a => a.answer === answerKey);



// WEBPACK FOOTER //
// ./public/pages/UserManagement/utils.js